import { Badge } from "@/components/ui/badge";
import { 
  BookOpen,
  Twitter,
  Linkedin,
  Video,
  Mic,
  Mail,
  MessageSquare,
  Plus,
  ArrowRight,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { cn } from "@/lib/utils";
import { GeneratedContent, Platform } from "@/types/content";
import { ContentCard } from "./ContentCard";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface ContentGridProps {
  contents: GeneratedContent[];
  onDelete: (id: string) => Promise<void>;
  onPost: (id: string) => Promise<void>;
  deletingId: string | null;
  postingId: string | null;
  onCardClick: (id: string) => void;
  highlightedContentId?: string | null;
  collapsedSections?: Record<string, boolean>;
  setCollapsedSections?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

// Platform configurations
const PLATFORMS: {
  id: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  description: string;
  colorBg: string;
  colorText: string;
  colorBorder: string;
  darkColorBg: string;
  darkColorText: string;
  darkColorBorder: string;
}[] = [
  {
    id: 'blog',
    label: 'Blog Posts',
    icon: BookOpen,
    description: 'Long-form content for your blog',
    colorBg: 'bg-blue-50',
    colorText: 'text-blue-700',
    colorBorder: 'border-blue-200',
    darkColorBg: 'dark:bg-blue-900/20',
    darkColorText: 'dark:text-blue-300',
    darkColorBorder: 'dark:border-blue-800/30',
  },
  {
    id: 'twitter',
    label: 'X.com Posts',
    icon: Twitter,
    description: 'Short-form updates for X.com',
    colorBg: 'bg-blue-50',
    colorText: 'text-blue-700',
    colorBorder: 'border-blue-200',
    darkColorBg: 'dark:bg-blue-900/20',
    darkColorText: 'dark:text-blue-300',
    darkColorBorder: 'dark:border-blue-800/30',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn Posts',
    icon: Linkedin,
    description: 'Professional content for LinkedIn',
    colorBg: 'bg-sky-50',
    colorText: 'text-sky-700',
    colorBorder: 'border-sky-200',
    darkColorBg: 'dark:bg-sky-900/20',
    darkColorText: 'dark:text-sky-300',
    darkColorBorder: 'dark:border-sky-800/30',
  },
  {
    id: 'email',
    label: 'Email Content',
    icon: Mail,
    description: 'Engaging emails for your campaigns',
    colorBg: 'bg-emerald-50',
    colorText: 'text-emerald-700',
    colorBorder: 'border-emerald-200',
    darkColorBg: 'dark:bg-emerald-900/20',
    darkColorText: 'dark:text-emerald-300',
    darkColorBorder: 'dark:border-emerald-800/30',
  },
  {
    id: 'video',
    label: 'Video Scripts',
    icon: Video,
    description: 'Engaging scripts for viral videos',
    colorBg: 'bg-red-50',
    colorText: 'text-red-700',
    colorBorder: 'border-red-200',
    darkColorBg: 'dark:bg-red-900/20',
    darkColorText: 'dark:text-red-300',
    darkColorBorder: 'dark:border-red-800/30',
  },
  {
    id: 'podcast',
    label: 'Podcast Scripts',
    icon: Mic,
    description: 'Audio scripts for podcast episodes',
    colorBg: 'bg-amber-50',
    colorText: 'text-amber-700',
    colorBorder: 'border-amber-200',
    darkColorBg: 'dark:bg-amber-900/20',
    darkColorText: 'dark:text-amber-300',
    darkColorBorder: 'dark:border-amber-800/30',
  },
  {
    id: 'reddit', 
    label: 'Reddit Posts',
    icon: MessageSquare,
    description: 'Engaging content for Reddit communities',
    colorBg: 'bg-orange-50',
    colorText: 'text-orange-700',
    colorBorder: 'border-orange-200',
    darkColorBg: 'dark:bg-orange-900/20',
    darkColorText: 'dark:text-orange-300',
    darkColorBorder: 'dark:border-orange-800/30',
  },
  {
    id: 'instagram',
    label: 'Instagram Content',
    icon: Video,
    description: 'Visual content for Instagram',
    colorBg: 'bg-pink-50',
    colorText: 'text-pink-700',
    colorBorder: 'border-pink-200',
    darkColorBg: 'dark:bg-pink-900/20',
    darkColorText: 'dark:text-pink-300',
    darkColorBorder: 'dark:border-pink-800/30',
  }
];

export const ContentGrid = ({ 
  contents, 
  onDelete, 
  onPost, 
  deletingId, 
  postingId,
  onCardClick,
  highlightedContentId,
  collapsedSections: externalCollapsedSections,
  setCollapsedSections: externalSetCollapsedSections
}: ContentGridProps) => {
  const navigate = useNavigate();
  // Add state to track collapsed sections - use external if provided
  const [internalCollapsedSections, setInternalCollapsedSections] = useState<Record<string, boolean>>({});
  
  const collapsedSections = externalCollapsedSections || internalCollapsedSections;
  const setCollapsedSections = externalSetCollapsedSections || setInternalCollapsedSections;
  
  // Load collapsed state from localStorage on mount - only if not using external state
  useEffect(() => {
    if (!externalCollapsedSections) {
      try {
        const savedState = localStorage.getItem('collapsedContentSections');
        if (savedState) {
          setInternalCollapsedSections(JSON.parse(savedState));
        }
      } catch (error) {
        console.error('Error loading collapsed sections state:', error);
      }
    }
  }, [externalCollapsedSections]);
  
  // Save collapsed state to localStorage when it changes - only if not using external state
  useEffect(() => {
    if (!externalCollapsedSections) {
      try {
        localStorage.setItem('collapsedContentSections', JSON.stringify(internalCollapsedSections));
      } catch (error) {
        console.error('Error saving collapsed sections state:', error);
      }
    }
  }, [internalCollapsedSections, externalCollapsedSections]);
  
  const getContentsByPlatform = (platform: string) => 
    contents.filter(content => content.platform === platform);
    
  // Toggle section collapsed state
  const toggleSection = (platformId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [platformId]: !prev[platformId]
    }));
  };

  return (
    <div className="grid grid-cols-1 gap-8">
      {/* Render known platforms from PLATFORMS array */}
      {PLATFORMS.filter(platform => 
        // Only include the allowed platforms
        ['blog', 'twitter', 'linkedin', 'video'].includes(platform.id) && 
        // Only show platforms that have content
        getContentsByPlatform(platform.id).length > 0
      ).map(({ id, label, colorBg, colorText, colorBorder, darkColorBg, darkColorText, darkColorBorder, icon: Icon, description }) => {
        const platformContents = getContentsByPlatform(id);
        const hasContent = platformContents.length > 0;
        const isCollapsed = collapsedSections[id] === true;
        
        return (
          <div key={id} className="w-full">
            <div 
              className={cn(
                "mb-4 p-4 rounded-xl border shadow-sm cursor-pointer transition-all",
                colorBg, colorBorder, darkColorBg, darkColorBorder,
                isCollapsed ? "" : "mb-3",
                "hover:brightness-95 dark:hover:brightness-110 hover:shadow-md"
              )}
              onClick={() => toggleSection(id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn("p-2.5 rounded-lg", colorBg, darkColorBg, "bg-opacity-70 dark:bg-opacity-30")}>
                    <Icon className={cn("w-5 h-5", colorText, darkColorText)} />
                  </div>
                  <div>
                    <h3 className={cn("font-semibold text-lg", colorText, darkColorText)}>
                      {label}
                    </h3>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">{description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={cn(
                    "text-sm py-1 px-3 rounded-full", 
                    colorBg, colorText, colorBorder, 
                    darkColorBg, darkColorText, darkColorBorder,
                    "bg-opacity-70 dark:bg-opacity-30"
                  )}>
                    {platformContents.length} {platformContents.length === 1 ? 'item' : 'items'}
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className={cn(
                      "rounded-full", 
                      colorText, 
                      darkColorText,
                      "hover:bg-opacity-10 hover:bg-zinc-500"
                    )}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent toggling when clicking the button
                      // Get project ID from URL
                      const projectId = window.location.pathname.split('/')[2];
                      navigate(`/projects/${projectId}/topics?platform=${id}`);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add New
                  </Button>
                  <motion.div
                    initial={false}
                    animate={{ rotate: isCollapsed ? 0 : 180 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className={cn("h-5 w-5", colorText, darkColorText)} />
                  </motion.div>
                </div>
              </div>
            </div>

            <AnimatePresence>
              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  {hasContent ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {platformContents.map((content) => (
                        <ContentCard
                          key={content.id}
                          content={content}
                          onDelete={onDelete}
                          onPost={onPost}
                          isDeleting={deletingId === content.id}
                          isPosting={postingId === content.id}
                          onClick={() => onCardClick(content.id)}
                          showActions={true}
                          isHighlighted={highlightedContentId === content.id}
                          id={`content-card-${content.id}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={cn(
                      "p-8 text-center border-2 border-dashed rounded-xl flex flex-col items-center justify-center",
                      "bg-zinc-50 dark:bg-zinc-800/30",
                      "border-zinc-200 dark:border-zinc-700"
                    )}>
                      <div className={cn("p-3 rounded-full mb-3", colorBg, darkColorBg, "bg-opacity-50")}>
                        <Icon className={cn("w-6 h-6", colorText, darkColorText)} />
                      </div>
                      <h4 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">No {label} Yet</h4>
                      <p className="text-zinc-500 dark:text-zinc-400 max-w-md mb-4">
                        Create your first {label.toLowerCase()} to engage your audience with professional content.
                      </p>
                      <Button 
                        className={cn(
                          colorBg, colorText, colorBorder,
                          darkColorBg, darkColorText, darkColorBorder,
                          "hover:bg-opacity-80"
                        )}
                        onClick={() => {
                          // Get project ID from URL
                          const projectId = window.location.pathname.split('/')[2];
                          navigate(`/projects/${projectId}/topics?platform=${id}`);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create {label}
                      </Button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
      
      {/* Render any content with platforms not in the PLATFORMS array */}
      {(() => {
        // Get all platform IDs from PLATFORMS array
        const knownPlatformIds = PLATFORMS.map(p => p.id);
        
        // Define allowed platforms
        const allowedPlatforms = ['blog', 'twitter', 'linkedin', 'video'];
        
        // Filter content with unknown platforms that are in the allowed list
        const unknownPlatformContents = contents.filter(
          content => content.platform && 
                      !knownPlatformIds.includes(content.platform) && 
                      allowedPlatforms.includes(content.platform)
        );
        
        // Group by platform
        const groupedByPlatform: Record<string, GeneratedContent[]> = {};
        unknownPlatformContents.forEach(content => {
          if (!groupedByPlatform[content.platform]) {
            groupedByPlatform[content.platform] = [];
          }
          groupedByPlatform[content.platform].push(content);
        });
        
        // Render each unknown platform group
        return Object.entries(groupedByPlatform).map(([platformId, platformContents]) => {
          const hasContent = platformContents.length > 0;
          const isCollapsed = collapsedSections[platformId] === true;
          
          return (
            <div key={platformId} className="w-full">
              <div 
                className={cn(
                  "mb-4 p-4 rounded-xl border shadow-sm cursor-pointer transition-all",
                  "bg-zinc-50 border-zinc-200 dark:bg-zinc-800/50 dark:border-zinc-700",
                  isCollapsed ? "" : "mb-3",
                  "hover:brightness-95 dark:hover:brightness-110 hover:shadow-md"
                )}
                onClick={() => toggleSection(platformId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 bg-opacity-70 dark:bg-opacity-30">
                      <Plus className="w-5 h-5 text-zinc-700 dark:text-zinc-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-zinc-700 dark:text-zinc-400">
                        {platformId.charAt(0).toUpperCase() + platformId.slice(1)} Content
                      </h3>
                      <p className="text-sm text-zinc-600 dark:text-zinc-400">Other content types</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="text-sm py-1 px-3 rounded-full bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800/50 dark:text-zinc-300 dark:border-zinc-700">
                      {platformContents.length} {platformContents.length === 1 ? 'item' : 'items'}
                    </Badge>
                    <motion.div
                      initial={false}
                      animate={{ rotate: isCollapsed ? 0 : 180 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="h-5 w-5 text-zinc-700 dark:text-zinc-400" />
                    </motion.div>
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {!isCollapsed && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    {hasContent ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {platformContents.map((content) => (
                          <ContentCard
                            key={content.id}
                            content={content}
                            onDelete={onDelete}
                            onPost={onPost}
                            isDeleting={deletingId === content.id}
                            isPosting={postingId === content.id}
                            onClick={() => onCardClick(content.id)}
                            showActions={true}
                            isHighlighted={highlightedContentId === content.id}
                            id={`content-card-${content.id}`}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-zinc-50 dark:bg-zinc-900/50 rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <p className="text-zinc-500 dark:text-zinc-400">No content for this platform yet</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        });
      })()}
    </div>
  );
}; 